import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Stepper, PageSubtitle } from "../components/lux";
import StepClient from "../components/booking/StepClient";
import StepSchedule from "../components/booking/StepSchedule";
import StepDetails from "../components/booking/StepDetails";
import StepReview from "../components/booking/StepReview";
import { stepIssues, hasIssues } from "../components/booking/validation";
import {
  RENTAL_FIELDS, CHAUFFEUR_FIELDS, TOUR_FIELDS, INTERNAL_FIELDS, CLIENT_CREDENTIAL_FIELDS,
  SALESPERSON_OPTIONS, validateBooking, todayISODate, computeChargesTotal, remainingBalance, billableServiceHours, inclusiveDays, hourPeriods,
} from "./bookingFields";
import { isChauffeurOnly } from "../lib/permissions";
import { driverPayFor } from "../lib/chauffeurSplit";
import { LeadMatchDialog, useLeadMatch, LEAD_MATCH_BLOCK_REASON } from "../components/LeadMatchDialog";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle, Trash, FloppyDisk } from "@phosphor-icons/react";

const DRAFT_KEY = "01ag_booking_wizard_draft";
const LAST_PICKUP_KEY = "01ag_last_pickup";

const STEPS = [
  { key: "client", label: "Type & Client" },
  { key: "schedule", label: "Dates & Vehicle" },
  { key: "details", label: "Details & Pricing" },
  { key: "review", label: "Review" },
];
// SALES-022: an internal hold skips the client and pricing steps entirely.
const INTERNAL_STEPS = [
  { key: "client", label: "Type & Use" },
  { key: "schedule", label: "Dates & Vehicle" },
  { key: "review", label: "Review" },
];
const stepsFor = (type) => (type === "internal" ? INTERNAL_STEPS : STEPS);

const fieldsFor = (type) => (type === "rental" ? RENTAL_FIELDS : type === "tour" ? TOUR_FIELDS : type === "internal" ? INTERNAL_FIELDS : CHAUFFEUR_FIELDS);

function blankFor(type, defaults = {}) {
  const extra = type === "internal" ? [] : CLIENT_CREDENTIAL_FIELDS;
  const blank = Object.fromEntries(
    [...fieldsFor(type), ...extra].map((f) => [f.name, f.default ?? (f.type === "number" ? 0 : "")])
  );
  return { ...blank, booking_type: type, status: "pre_booking", booking_date: todayISODate(), ...defaults };
}

export default function NewBookingWizard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const duplicateId = params.get("duplicate");
  const initialType = params.get("type");
  const chauffeurLocked = isChauffeurOnly(user);
  const moneyVisible = user?.role === "admin" || user?.role === "staff";

  const [vehicles, setVehicles] = useState([]);
  const [chauffeurs, setChauffeurs] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [salesOptions, setSalesOptions] = useState(SALESPERSON_OPTIONS);
  const startType = chauffeurLocked ? "chauffeur" : (["rental", "chauffeur", "tour", "internal"].includes(initialType) ? initialType : "rental");
  const [formType, setFormType] = useState(startType);
  const [form, setForm] = useState(() => blankFor(startType, {
    salesperson: user?.salesperson || "",
    start_location: localStorage.getItem(LAST_PICKUP_KEY) || "",
  }));
  const [flightNo, setFlightNo] = useState("");
  const [step, setStep] = useState(0);
  const [attempted, setAttempted] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [draftRestored, setDraftRestored] = useState(false);
  const [bookedIds, setBookedIds] = useState([]);
  const [checkingAvail, setCheckingAvail] = useState(false);
  const hydrated = useRef(false);
  const steps = stepsFor(formType);

  /* ---------- reference data ---------- */
  useEffect(() => {
    (async () => {
      const [v, c] = await Promise.all([
        api.get("/vehicles").catch(() => ({ data: [] })),
        api.get("/chauffeurs").catch(() => ({ data: [] })),
      ]);
      setVehicles(v.data || []);
      setChauffeurs(c.data || []);
      try {
        const { data } = await api.get("/salespeople");
        const names = (data || []).filter((s) => s.active !== false).map((s) => s.name);
        if (names.length) setSalesOptions(names);
      } catch { /* static fallback */ }
      try {
        const { data } = await api.get("/bookings");
        setBookings(data || []);
      } catch { setBookings([]); }
    })();
  }, []);

  /* ---------- draft restore / duplicate prefill ---------- */
  useEffect(() => {
    if (hydrated.current) return;
    hydrated.current = true;
    if (duplicateId) {
      (async () => {
        try {
          const { data } = await api.get(`/bookings/${duplicateId}`);
          const type = data.booking_type === "rental" ? "rental" : data.booking_type === "tour" ? "tour" : data.booking_type === "internal" ? "internal" : "chauffeur";
          const clean = { ...data };
          // SALES-021: `lead_id` and `lead_match` belong to the booking that was
          // copied, not to this one. Inheriting them linked two bookings to one
          // lead and recorded a decision nobody made.
          ["id", "_id", "driver_paid", "driver_paid_at", "driver_pay_amount", "split_confirmed", "split_confirmed_at",
            "split_confirmed_by", "payment_status", "invoice_data", "rejection_reason",
            "lead_id", "lead_match",
            // SALES-023: a duplicate is a NEW booking — it re-derives its days
            // (and therefore its total) from the dates, like any other new one.
            "rental_days", "rental_days_manual"].forEach((k) => delete clean[k]);
          setFormType(type);
          setForm({ ...blankFor(type), ...clean, status: "pre_booking", source: "internal", booking_date: todayISODate() });
          toast.success("Booking duplicated — review and submit");
        } catch (e) { toast.error(formatApiError(e)); }
      })();
      return;
    }
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (!saved?.form || Date.now() - (saved.at || 0) > 86400000) { localStorage.removeItem(DRAFT_KEY); return; }
      setFormType(saved.formType || "rental");
      setForm(saved.form);
      setFlightNo(saved.flightNo || "");
      setStep(Math.min(saved.step || 0, stepsFor(saved.formType || "rental").length - 1));
      setDraftRestored(true);
    } catch { localStorage.removeItem(DRAFT_KEY); }
  }, [duplicateId]);

  /* ---------- autosave ---------- */
  useEffect(() => {
    if (!hydrated.current || created) return;
    const t = setTimeout(() => {
      const touched = form.customer_name || form.vehicle_id || form.start_datetime;
      if (!touched) return;
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ formType, form, flightNo, step, at: Date.now() }));
    }, 600);
    return () => clearTimeout(t);
  }, [form, formType, flightNo, step, created]);

  /* ---------- availability for the picked window ---------- */
  const { start_datetime: start, end_datetime: end } = form;
  const datesReady = Boolean(start && end && start < end);
  useEffect(() => {
    if (!datesReady) { setBookedIds([]); return; }
    let cancelled = false;
    setCheckingAvail(true);
    (async () => {
      try {
        const { data } = await api.get("/vehicles/availability", { params: { start, end } });
        if (!cancelled) setBookedIds(data.booked_vehicle_ids || []);
      } catch { if (!cancelled) setBookedIds([]); }
      finally { if (!cancelled) setCheckingAvail(false); }
    })();
    return () => { cancelled = true; };
  }, [start, end, datesReady]);

  /* ---------- auto service hours (chauffeur / tour) ---------- */
  useEffect(() => {
    if (formType === "rental" || !datesReady || form.service_hours_manual) return;
    const windowHours = Math.round(((Date.parse(end) - Date.parse(start)) / 3600000) * 10) / 10;
    if (!(windowHours > 0)) return;
    // Standby comes out of the billable hours — it is time inside this window.
    const hrs = billableServiceHours(windowHours, form, formType);
    if (Number(form.service_hours) !== hrs) setForm((f) => ({ ...f, service_hours: hrs }));
  }, [start, end, datesReady, formType, form.service_hours_manual, form.service_hours, form.standby_hours]);

  /* ---------- auto rental days (rental) — 24-hour periods (SALES-029) ---------- */
  useEffect(() => {
    if (formType !== "rental" || !datesReady || form.rental_days_manual) return;
    const days = hourPeriods(start, end);
    if (days > 0 && Number(form.rental_days) !== days) setForm((f) => ({ ...f, rental_days: days }));
  }, [start, end, datesReady, formType, form.rental_days_manual, form.rental_days]);

  /* ---------- auto total ---------- */
  useEffect(() => {
    if (form.total_manual) return;
    const auto = computeChargesTotal(form, formType);
    if (auto > 0 && Number(form.total) !== auto) setForm((f) => ({ ...f, total: auto }));
  }, [form, formType]);

  /* ---------- remaining balance is always Total − Deposit ---------- */
  useEffect(() => {
    if (formType === "rental") return;
    const bal = remainingBalance(form);
    if (Number(form.remaining_balance) !== bal) setForm((f) => ({ ...f, remaining_balance: bal }));
  }, [form.total, form.depo_sent, form.remaining_balance, formType]);

  const onVehicleChange = useCallback((id) => {
    const v = vehicles.find((x) => x.id === id);
    setForm((f) => {
      const next = {
        ...f,
        vehicle_id: id,
        vehicle_label: v ? `${v.make} ${v.model}${v.year ? " " + v.year : ""}` : "",
        vehicle_plate: v?.license_plate || "",
      };
      // Suggest the fleet rate when nothing has been priced yet (editable).
      if (v) {
        if (formType === "rental" && !(Number(f.rental_rate) > 0) && Number(v.daily_rate) > 0) next.rental_rate = v.daily_rate;
        if (formType !== "rental" && !(Number(f.hourly_rate) > 0) && Number(v.hourly_rate) > 0) next.hourly_rate = v.hourly_rate;
      }
      return next;
    });
  }, [vehicles, formType]);

  const onDriverChange = useCallback((id) => {
    const d = chauffeurs.find((x) => x.id === id);
    setForm((f) => {
      const pay = driverPayFor({ ...f, booking_type: formType }, d);
      return {
        ...f,
        driver_id: id,
        driver_name: d ? `${d.first_name || ""} ${d.last_name || ""}`.trim() : "",
        driver_cost: Number(f.driver_cost) > 0 ? f.driver_cost : (pay || f.driver_cost),
      };
    });
  }, [chauffeurs, formType]);

  const recentLocations = useMemo(() => {
    const seen = [];
    for (const b of bookings) {
      for (const loc of [b.start_location, b.end_location]) {
        const s = String(loc || "").trim();
        if (s && !seen.includes(s)) seen.push(s);
      }
      if (seen.length >= 8) break;
    }
    return seen;
  }, [bookings]);

  const issues = stepIssues(step, form, formType);
  const showErrors = attempted.has(step);
  const errors = showErrors ? issues : {};

  const switchType = (type) => {
    setFormType(type);
    if (type === "internal") {
      // SALES-022: a hold has no client, salesperson or lead. Clear everything
      // the previous type carried and reset the lead-match hook, so the step-1
      // lead notice and the save-time dialog can never fire for an internal
      // booking. The server enforces the same and cannot be bypassed.
      leadMatch.reset();
      setForm((f) => blankFor("internal", {
        start_datetime: f.start_datetime,
        end_datetime: f.end_datetime,
        start_location: f.start_location,
      }));
      setStep((s) => Math.min(s, INTERNAL_STEPS.length - 1));
      return;
    }
    setForm((f) => blankFor(type, {
      salesperson: f.salesperson,
      customer_id: f.customer_id,
      customer_name: f.customer_name,
      customer_first_name: f.customer_first_name,
      customer_last_name: f.customer_last_name,
      customer_phone: f.customer_phone,
      customer_email: f.customer_email,
      // The phone comes across, so the lead decision made about that phone has
      // to come with it. Without this the hook still held the link and the save
      // was right, but the autosaved draft did not — so a type switch followed
      // by a reload lost the link silently.
      lead_id: f.lead_id,
      start_datetime: f.start_datetime,
      end_datetime: f.end_datetime,
      start_location: f.start_location,
    }));
  };

  const next = () => {
    // The lead question holds Continue, and only while it is on screen and
    // unanswered. A slow or failed lookup never gets here. An internal hold has
    // no lead question at all.
    if (step === 0 && formType !== "internal" && leadMatch.inline.blocked) {
      toast.error(LEAD_MATCH_BLOCK_REASON);
      return;
    }
    if (hasIssues(issues)) {
      setAttempted((s) => new Set(s).add(step));
      toast.error(Object.values(issues)[0]);
      return;
    }
    setStep((s) => Math.min(steps.length - 1, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const discardDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    leadMatch.reset();
    setForm(blankFor(formType, { salesperson: user?.salesperson || "" }));
    setFlightNo("");
    setStep(0);
    setDraftRestored(false);
    toast.success("Draft discarded");
  };

  const submit = async () => {
    const payload = { ...form, booking_type: formType };
    // SALES-022: an internal hold is `confirmed` on creation (the server forces
    // this regardless); send it explicitly so client and server never drift.
    if (formType === "internal") payload.status = "confirmed";
    if (flightNo.trim()) payload.notes = `Flight: ${flightNo.trim()}${payload.notes ? `\n${payload.notes}` : ""}`;
    const err = validateBooking(payload, formType, { isNew: true });
    if (err) { toast.error(err); return; }
    for (const f of fieldsFor(formType)) {
      if (f.type === "number" && payload[f.name] !== "" && payload[f.name] !== null) {
        payload[f.name] = Number(payload[f.name]) || 0;
      }
    }
    // SALES-017: does this customer already exist as a lead? The dialog decides
    // whether to link, then hands the payload straight back to `doSave`.
    leadMatch.start(payload);
  };

  const doSave = async (payload, { linked } = {}) => {
    setSaving(true);
    try {
      const { data } = await api.post("/bookings", payload);
      if (payload.start_location) localStorage.setItem(LAST_PICKUP_KEY, payload.start_location);
      localStorage.removeItem(DRAFT_KEY);
      setCreated({ ...payload, id: data?.id });
      // SALES-018: the booking came back with a lead_id it was not sent, so the
      // server created the lead behind it.
      const leadMade = !payload.lead_id && !!data?.lead_id;
      const fromDate = data?.auto_linked?.came_in
        ? new Date(data.auto_linked.came_in).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "America/Toronto" })
        : "";
      toast.success(
        payload.booking_type === "internal" ? "Internal hold created — the car is blocked for these dates"
          : data?.auto_linked ? `Booking submitted — linked to the lead from ${fromDate}`
            : linked ? "Booking submitted and linked to the lead"
              : leadMade ? "Booking submitted — a lead was created for this customer"
                : "Booking submitted",
      );
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  // SALES-021: the hook owns the decision, the page owns the form. Stable, or
  // `check` changes on every render and the debounce never lands.
  const onLinkChange = useCallback((leadId) => {
    setForm((f) => (f.lead_id === leadId ? f : { ...f, lead_id: leadId }));
  }, []);
  const leadMatch = useLeadMatch(doSave, { onLinkChange });

  // SALES-021: a draft can be a day old. If it carries a link, check the lead is
  // still a candidate — the lookup excludes converted and lost leads — rather
  // than saving a booking against a lead that has since become something else.
  useEffect(() => {
    if (!draftRestored || !form.lead_id) return;
    (async () => {
      const ok = await leadMatch.revalidate(form.lead_id, form.customer_phone);
      if (!ok) toast.error("The lead this draft was linked to is no longer open — the link was removed.");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftRestored]);

  const startAnother = () => {
    setCreated(null);
    // SALES-021: the next booking is a different customer until proven otherwise.
    leadMatch.reset();
    setForm(blankFor(formType, { salesperson: form.salesperson, start_location: localStorage.getItem(LAST_PICKUP_KEY) || "" }));
    setFlightNo("");
    setAttempted(new Set());
    setStep(0);
  };

  const ctx = {
    vehicles, chauffeurs, bookings, salesOptions, moneyVisible, chauffeurLocked,
    bookedIds, checkingAvail, onVehicleChange, onDriverChange, recentLocations,
    flightNo, setFlightNo, user,
    leadMatch,   // SALES-021 — StepClient draws the notice under the phone field
  };

  // Held, not disabled-looking-for-no-reason: the why is printed beside it.
  const leadBlocked = step === 0 && formType !== "internal" && leadMatch.inline.blocked;

  if (created) {
    const isInternalCreated = created.booking_type === "internal";
    // SALES-023: "View on calendar" is the primary action — Fleet Availability
    // on the booking's start month, with its car and start date highlighted.
    // A booking with no vehicle still links to the calendar on its month.
    const startDT = created.start_datetime ? new Date(created.start_datetime) : null;
    const validDT = startDT && !Number.isNaN(startDT.getTime());
    const p = new URLSearchParams({ tab: "availability" });
    if (validDT) {
      const mo = `${startDT.getFullYear()}-${String(startDT.getMonth() + 1).padStart(2, "0")}`;
      p.set("month", mo);
      p.set("day", `${mo}-${String(startDT.getDate()).padStart(2, "0")}`);
    }
    if (created.vehicle_id) p.set("vehicle", created.vehicle_id);
    const calUrl = `/calendar?${p.toString()}`;
    return (
      <div className="max-w-2xl mx-auto" data-testid="booking-created-panel">
        <div className="border border-emerald-500/30 bg-emerald-500/[0.04] p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto mb-5">
            <CheckCircle size={24} className="text-emerald-400" weight="fill" />
          </div>
          <h2 className="font-heading text-3xl">{isInternalCreated ? "Internal hold created" : "Booking submitted"}</h2>
          <p className="text-sm text-muted-foreground mt-2.5">
            {isInternalCreated ? (
              <>
                {created.internal_user || "Internal use"} · {created.vehicle_label || "vehicle TBD"} · the car is now
                {" "}<b className="text-foreground">blocked</b> on the calendar for these dates and counts toward nothing.
              </>
            ) : (
              <>
                {created.customer_name} · {created.vehicle_label || "vehicle TBD"} · it's now in the
                {" "}<b className="text-foreground">Submitted Bookings</b> queue awaiting approval.
              </>
            )}
          </p>
          <div className="flex items-center justify-center gap-2 mt-7 flex-wrap">
            <Button onClick={() => navigate(calUrl)} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="created-view-calendar">
              View on calendar
            </Button>
            <Button variant="outline" onClick={startAnother} data-testid="created-new-booking">
              Create another booking
            </Button>
            {!isInternalCreated && (
              <Button variant="ghost" onClick={() => navigate(moneyVisible ? "/bookings/submitted" : "/my-bookings")} data-testid="created-view-queue">
                {moneyVisible ? "Open approval queue" : "View my bookings"}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto pb-24" data-testid="new-booking-page">
      <PageSubtitle>
        {steps.length} quick steps · availability-aware · saved as you go
      </PageSubtitle>

      {draftRestored && (
        <div className="flex items-center justify-between gap-3 flex-wrap mb-6 rounded-2xl border border-primary/30 bg-primary/[0.05] px-5 py-3" data-testid="draft-restored-bar">
          <span className="text-sm flex items-center gap-2"><FloppyDisk size={15} className="text-primary" /> Draft restored from your last session.</span>
          <Button size="sm" variant="ghost" onClick={discardDraft} data-testid="discard-draft"><Trash size={13} className="mr-1.5" />Discard</Button>
        </div>
      )}

      <div className="border border-border bg-card p-5 sm:p-7">
        {/* Forward is only ever the Continue button — the stepper is
            backwards-only (`i <= current`), so it needs no lead gate. */}
        <Stepper steps={steps} current={step} onStep={setStep} testid="booking-stepper" />
        <div className="mt-8">
          {steps[step].key === "client" && <StepClient formType={formType} onTypeChange={switchType} form={form} setForm={setForm} errors={errors} ctx={ctx} />}
          {steps[step].key === "schedule" && <StepSchedule formType={formType} form={form} setForm={setForm} errors={errors} ctx={ctx} />}
          {steps[step].key === "details" && <StepDetails formType={formType} form={form} setForm={setForm} errors={errors} ctx={ctx} />}
          {steps[step].key === "review" && <StepReview formType={formType} form={form} setForm={setForm} onStep={setStep} ctx={ctx} />}
        </div>
      </div>

      {/* Sticky action bar */}
      <div className="fixed bottom-0 left-0 right-0 md:left-64 z-20 border-t border-border bg-background/90 backdrop-blur-xl px-4 sm:px-8 py-3">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Button variant="ghost" onClick={back} disabled={step === 0} data-testid="wizard-back">
            <ArrowLeft size={15} className="mr-1.5" /> Back
          </Button>
          <div className="flex-1 text-center label-mono hidden sm:block">
            Step {step + 1} of {steps.length} · {steps[step].label}
          </div>
          {leadBlocked && (
            <span className="text-[11px] sm:text-xs text-amber-200/90 text-right leading-snug max-w-[240px]"
                  data-testid="wizard-next-blocked-reason">
              {LEAD_MATCH_BLOCK_REASON}
            </span>
          )}
          {step < steps.length - 1 ? (
            <Button
              onClick={next}
              disabled={leadBlocked}
              title={leadBlocked ? LEAD_MATCH_BLOCK_REASON : undefined}
              className="bg-primary text-primary-foreground hover:bg-primary/90 min-w-[120px] disabled:opacity-40"
              data-testid="wizard-next"
            >
              Continue <ArrowRight size={15} className="ml-1.5" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90 min-w-[150px]" data-testid="new-booking-save">
              {saving ? "Submitting…" : "Submit booking"}
            </Button>
          )}
        </div>
      </div>

      {/* SALES-017: shown only when the phone matches an unlinked, live lead. */}
      <LeadMatchDialog {...leadMatch.dialog} />
    </div>
  );
}
